import * as React from "react";
import { Context } from "../appcontext";
import { AlphabetMode } from "../common/communication.base";
import { NclVirtualKeyboardDialog } from "../common/components.ncl";
import { K2Button } from "./K2Button";
import { AcquireControl, withContext, WithContextPlacementProps } from "./k2hoc";
import { K2Img } from "./K2Image";
import { K2Input } from "./K2Input";
import { K2RuleWithVCXProps } from "./k2StyleRenderer";

const mainRule = (props: K2RuleWithVCXProps) => ({
  height: "100%",
  width: "100%",
  backgroundColor: props.vcx.getColor(props.vcx.Data.ColorMap.ContentFrame1),
  flexDirection: "column" as "column",
});

type ContentRule = K2RuleWithVCXProps & {
  isNumeric: boolean;
  fill: boolean;
};

const contentRule = (props: ContentRule) => ({
  flex: "1 0 auto",
  flexDirection: "column" as "column",
  justifyContent: "flex-end",
});

type KeyRuleProps = K2RuleWithVCXProps & {
  ratio: number;
  hidden?: boolean;
};
const keyRule = (props: KeyRuleProps) => ({
  flex: `${props.ratio} 1 0%`,
  visibility: props.hidden === true ? ("hidden" as "hidden") : ("visible" as "visible"),
});

type EmptyRuleProps = K2RuleWithVCXProps & {
  multiplier: number;
};

const emptyRule = (props: EmptyRuleProps) => ({
  height: `${props.vcx.GridControl.GetRowHeight(props.multiplier)}px`,
  flex: "0 0 auto",
});

const imageRule = (props: K2RuleWithVCXProps) => ({
  padding: props.vcx.cssSizeMap(2, "px"),
});

const inputRule = (props: K2RuleWithVCXProps) => ({
  flex: "1",
});

const dialogsBtnRule = (props: K2RuleWithVCXProps) => ({
  flex: "0 0 auto",
  flexDirection: "row" as "row",
  height: `${props.vcx.InputControl.getInputHeight(2, true, false)}px`,
});

const partMinRule = (props: K2RuleWithVCXProps) => ({
  flex: "0 0 auto",
  flexDirection: "column" as "column",
});

const partRule = (props: K2RuleWithVCXProps) => ({
  flex: "0 0 auto",
  height: `${props.vcx.InputControl.getInputHeight(2, true, false)}px`,
});

interface RenderItem {
  value: string;
  ratio: number;
  hidden?: boolean;
}

interface VirtualKeyboardProps extends WithContextPlacementProps {
  mode: AlphabetMode;
}

interface VirtualKeyboardState {
  fill: boolean;
  data: JsonStructure;
}

interface JsonStructure {
  numericArray: RenderItem[][];
  alphabetArray: RenderItem[][];
  upAlphabetArray: RenderItem[][];
  numAlphabetArray: RenderItem[][];
}

class _VirtualKeyboard extends React.PureComponent<VirtualKeyboardProps, VirtualKeyboardState> {
  static displayName = `K2VirtualKeyboard`;
  private control: NclVirtualKeyboardDialog;
  private numericArray: Array<Array<RenderItem>> = [];
  private alphabetArray: Array<Array<RenderItem>> = [];
  private upAlphabetArray: Array<Array<RenderItem>> = [];
  private numAlphabetArray: Array<Array<RenderItem>> = [];

  constructor(props: VirtualKeyboardProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return true;
    }) as NclVirtualKeyboardDialog;
    this.state = { fill: Context.DeviceInfo.getOrientation() === "landscape", data: null };
  }

  async componentDidMount() {
    window.addEventListener("orientationchange", this.orientationchange);

    try {
      const resp = await fetch("./keyboard_settings.json");
      const data: JsonStructure = await resp.json();

      data.numericArray.map((nums) => {
        nums.map((num) => {
          if (num.value === ",") {
            num.hidden = this.control.Ncl.FrgtData.IsFloat === 0;
          }
        });
      });

      this.alphabetArray = data.alphabetArray;
      this.numericArray = data.numericArray;
      this.upAlphabetArray = data.upAlphabetArray;
      this.numAlphabetArray = data.numAlphabetArray;

      this.setState({ data: data });
    } catch (error) {
      console.log(error);
      this.initArrays();
      this.setState({
        data: {
          alphabetArray: this.alphabetArray,
          numAlphabetArray: this.numAlphabetArray,
          numericArray: this.numericArray,
          upAlphabetArray: this.upAlphabetArray,
        },
      });
    }
  }

  componentWillUnmount() {
    this.control = null;
    window.removeEventListener("orientationchange", this.orientationchange);
  }

  private orientationchange = (event: Event) => {
    this.setState({ fill: !this.state.fill });
  };

  render() {
    if (this.state.data == null) return null;

    return (
      <div className={this.props.renderer.renderRule(mainRule, { vcx: this.control.VCX })}>
        {this.renderImage()}
        <div
          className={this.props.renderer.renderRule(contentRule, {
            vcx: this.control.VCX,
            isNumeric: this.control.Ncl.FrgtData.IsNumeric === 1,
            fill: this.state.fill,
          })}
        >
          {this.renderKeyboard()}
        </div>
      </div>
    );
  }

  private renderKeyboard(): JSX.Element {
    let input = this.control.getKeyControlByValue(NclVirtualKeyboardDialog.INPUT);
    return (
      <>
        <div className={this.props.renderer.renderRule(partMinRule, { vcx: this.control.VCX })}>
          <div className={this.props.renderer.renderRule(inputRule, { vcx: this.control.VCX })}>
            {input && <K2Input controlUID={input.MetaData.ControlUID} vrUID={input.getRealizerUID()} blockSoftKeyboard={true} />}
          </div>
        </div>

        {this.renderContent()}
        <div className={this.props.renderer.renderRule(partMinRule, { vcx: this.control.VCX })}>
          {!this.state.fill && <div className={this.props.renderer.renderRule(emptyRule, { vcx: this.control.VCX, multiplier: 1 })} />}
        </div>
        <div className={this.props.renderer.renderRule(dialogsBtnRule, { vcx: this.control.VCX })}>
          {this.renderKeys([
            { value: NclVirtualKeyboardDialog.OK, ratio: 1 },
            { value: NclVirtualKeyboardDialog.CANCEL, ratio: 1 },
          ])}
        </div>
      </>
    );
  }
  private renderImage(): JSX.Element {
    if (this.state.fill || !this.control.Ncl.FrgtData.GlyphId) return null;
    return (
      <K2Img
        glyphId={this.control.Ncl.FrgtData.GlyphId}
        vcx={this.control.VCX}
        className={this.props.renderer.renderRule(imageRule, { vcx: this.control.VCX })}
      />
    );
  }

  private renderContent(): React.ReactNode {
    if (this.control.Ncl.FrgtData.IsNumeric === 1) {
      //num
      return (
        <>
          {this.state.data.numericArray.map((item, index) => {
            return (
              <div key={`key_part_${index}`} className={this.props.renderer.renderRule(partRule, { vcx: this.control.VCX })}>
                {this.renderKeys(item)}
              </div>
            );
          })}
        </>
      );
    } else {
      let array: Array<Array<RenderItem>>;
      switch (this.props.mode) {
        case AlphabetMode.upperCase:
          array = this.state.data.upAlphabetArray;
          break;
        case AlphabetMode.numeric:
          array = this.state.data.numAlphabetArray;
          break;

        default:
          array = this.state.data.alphabetArray;
          break;
      }

      return (
        <>
          {array.map((item, index) => {
            return (
              <div key={`key_part_${index}`} className={this.props.renderer.renderRule(partRule, { vcx: this.control.VCX })}>
                {this.renderKeys(item)}
              </div>
            );
          })}
        </>
      );
    }
  }

  private renderKeys(btns: Array<RenderItem>): Array<JSX.Element> {
    let result: Array<JSX.Element> = new Array<JSX.Element>();

    btns.map((item, index) => {
      if (!item) return;
      let ctrl = this.control.getKeyControlByValue(item.value);
      if (ctrl) {
        result.push(
          <div key={`key_${item.value}`} className={this.props.renderer.renderRule(keyRule, { vcx: this.control.VCX, ratio: item.ratio, hidden: item.hidden })}>
            <K2Button controlUID={ctrl.MetaData.ControlUID} vrUID={this.control.getRealizerUID()} fillSvg={true} />
          </div>
        );
      } else {
        result.push(
          <div
            key={`key_empty_${index}`}
            className={this.props.renderer.renderRule(keyRule, { vcx: this.control.VCX, ratio: item.ratio, hidden: item.hidden })}
          />
        );
      }
    });

    return result;
  }

  private initArrays() {
    this.numericArray = [
      [
        { value: "7", ratio: 2 },
        { value: "8", ratio: 2 },
        { value: "9", ratio: 2 },
      ],
      [
        { value: "4", ratio: 2 },
        { value: "5", ratio: 2 },
        { value: "6", ratio: 2 },
      ],
      [
        { value: "1", ratio: 2 },
        { value: "2", ratio: 2 },
        { value: "3", ratio: 2 },
      ],
      [
        { value: "0", ratio: 2 },
        { value: ",", ratio: 2, hidden: this.control.Ncl.FrgtData.IsFloat === 0 },
        { value: NclVirtualKeyboardDialog.NUM_CLEAR, ratio: 1 },
        { value: NclVirtualKeyboardDialog.NUM_CLEAR_ALL, ratio: 1 },
      ],
    ];

    this.alphabetArray = [
      [
        { value: "1", ratio: 2 },
        { value: "2", ratio: 2 },
        { value: "3", ratio: 2 },
        { value: "4", ratio: 2 },
        { value: "5", ratio: 2 },
        { value: "6", ratio: 2 },
        { value: "7", ratio: 2 },
        { value: "8", ratio: 2 },
        { value: "9", ratio: 2 },
        { value: "0", ratio: 2 },
      ],
      [
        { value: "q", ratio: 2 },
        { value: "w", ratio: 2 },
        { value: "e", ratio: 2 },
        { value: "r", ratio: 2 },
        { value: "t", ratio: 2 },
        { value: "y", ratio: 2 },
        { value: "u", ratio: 2 },
        { value: "i", ratio: 2 },
        { value: "o", ratio: 2 },
        { value: "p", ratio: 2 },
      ],
      [
        { value: undefined, ratio: 1 },
        { value: "a", ratio: 2 },
        { value: "s", ratio: 2 },
        { value: "d", ratio: 2 },
        { value: "f", ratio: 2 },
        { value: "g", ratio: 2 },
        { value: "h", ratio: 2 },
        { value: "j", ratio: 2 },
        { value: "k", ratio: 2 },
        { value: "l", ratio: 2 },
        { value: undefined, ratio: 1 },
      ],
      [
        { value: NclVirtualKeyboardDialog.UPPER_CASE, ratio: 2 },
        { value: "z", ratio: 2 },
        { value: "x", ratio: 2 },
        { value: "c", ratio: 2 },
        { value: "v", ratio: 2 },
        { value: "b", ratio: 2 },
        { value: "n", ratio: 2 },
        { value: "m", ratio: 2 },
        { value: NclVirtualKeyboardDialog.NUM_CLEAR, ratio: 2 },
      ],
      [
        { value: NclVirtualKeyboardDialog.NUM, ratio: 2 },
        { value: ",", ratio: 2 },
        { value: " ", ratio: 12 },
        { value: ".", ratio: 2 },
      ],
    ];

    this.upAlphabetArray = [
      [
        { value: "1", ratio: 2 },
        { value: "2", ratio: 2 },
        { value: "3", ratio: 2 },
        { value: "4", ratio: 2 },
        { value: "5", ratio: 2 },
        { value: "6", ratio: 2 },
        { value: "7", ratio: 2 },
        { value: "8", ratio: 2 },
        { value: "9", ratio: 2 },
        { value: "0", ratio: 2 },
      ],
      [
        { value: "Q", ratio: 2 },
        { value: "W", ratio: 2 },
        { value: "E", ratio: 2 },
        { value: "R", ratio: 2 },
        { value: "T", ratio: 2 },
        { value: "Y", ratio: 2 },
        { value: "U", ratio: 2 },
        { value: "I", ratio: 2 },
        { value: "O", ratio: 2 },
        { value: "P", ratio: 2 },
      ],
      [
        { value: undefined, ratio: 1 },
        { value: "A", ratio: 2 },
        { value: "S", ratio: 2 },
        { value: "D", ratio: 2 },
        { value: "F", ratio: 2 },
        { value: "G", ratio: 2 },
        { value: "H", ratio: 2 },
        { value: "J", ratio: 2 },
        { value: "K", ratio: 2 },
        { value: "L", ratio: 2 },
        { value: undefined, ratio: 1 },
      ],
      [
        { value: NclVirtualKeyboardDialog.LOWER_CASE, ratio: 2 },
        { value: "Z", ratio: 2 },
        { value: "X", ratio: 2 },
        { value: "C", ratio: 2 },
        { value: "V", ratio: 2 },
        { value: "B", ratio: 2 },
        { value: "N", ratio: 2 },
        { value: "M", ratio: 2 },
        { value: NclVirtualKeyboardDialog.NUM_CLEAR, ratio: 2 },
      ],
      [
        { value: NclVirtualKeyboardDialog.NUM, ratio: 2 },
        { value: ",", ratio: 2 },
        { value: " ", ratio: 12 },
        { value: ".", ratio: 2 },
      ],
    ];

    this.numAlphabetArray = [
      [
        { value: "1", ratio: 2 },
        { value: "2", ratio: 2 },
        { value: "3", ratio: 2 },
        { value: "4", ratio: 2 },
        { value: "5", ratio: 2 },
        { value: "6", ratio: 2 },
        { value: "7", ratio: 2 },
        { value: "8", ratio: 2 },
        { value: "9", ratio: 2 },
        { value: "0", ratio: 2 },
      ],
      [
        { value: "@", ratio: 2 },
        { value: "#", ratio: 2 },
        { value: "$", ratio: 2 },
        { value: "_", ratio: 2 },
        { value: "&", ratio: 2 },
        { value: "-", ratio: 2 },
        { value: "+", ratio: 2 },
        { value: "(", ratio: 2 },
        { value: ")", ratio: 2 },
        { value: "/", ratio: 2 },
      ],
      [
        { value: "=", ratio: 2 },
        { value: "*", ratio: 2 },
        { value: `"`, ratio: 2 },
        { value: "'", ratio: 2 },
        { value: ":", ratio: 2 },
        { value: ";", ratio: 2 },
        { value: "!", ratio: 2 },
        { value: "?", ratio: 2 },
        { value: "%", ratio: 2 },
        { value: NclVirtualKeyboardDialog.NUM_CLEAR, ratio: 2 },
      ],
      [
        { value: NclVirtualKeyboardDialog.ALPHABET, ratio: 2 },
        { value: ",", ratio: 2 },
        { value: " ", ratio: 14 },
        { value: ".", ratio: 2 },
      ],
    ];
  }
}

export const K2VirtualKeyboard = withContext(_VirtualKeyboard);
